import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Clock, Users, Flame, Gauge, Save, ChefHat, Loader2, Check, Globe } from "lucide-react";

const FALLBACK_THUMB = "https://images.unsplash.com/photo-1547592180-85f173990554?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

export default function ChatRecipeCard({ recipe, coverImage, creatorName, creatorHandle }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [publishedId, setPublishedId] = useState(null);
  const macros = recipe.macros || {};

  const save = async () => {
    if (savedId) return;
    setSaving(true);
    try {
      const { data } = await api.post("/recipes", {
        recipe, source: "user", creator_name: "You", thumbnail: coverImage || FALLBACK_THUMB,
      });
      setSavedId(data.id);
      toast.success("Saved to your recipes");
    } catch { toast.error("Could not save recipe"); } finally { setSaving(false); }
  };

  const publish = async () => {
    if (publishedId) return;
    setPublishing(true);
    try {
      const { data } = await api.post("/admin/recipes/publish", {
        recipe, source: "flavourai", thumbnail: coverImage || FALLBACK_THUMB,
        creator_name: creatorName || "Flavour Intelligence",
        creator_handle: creatorHandle || "@flavourintelligence",
      });
      setPublishedId(data.id);
      toast.success("Published to the home feed");
    } catch { toast.error("Could not publish recipe"); } finally { setPublishing(false); }
  };

  const stats = [
    { icon: Clock, val: `${recipe.total_time_min}m` },
    { icon: Users, val: recipe.servings },
    { icon: Gauge, val: recipe.difficulty },
    { icon: Flame, val: `${macros.calories || 0} kcal` },
  ];

  return (
    <div data-testid="chat-recipe-card" className="w-full rounded-2xl border border-black/10 bg-white overflow-hidden">
      {coverImage && (
        <div className="relative w-full aspect-[16/10] overflow-hidden bg-nerd-card">
          <img data-testid="chat-recipe-cover" src={mediaUrl(coverImage)} alt={recipe.title}
            className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-5">
        {recipe.cuisine && (
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-nerd-light/40">{recipe.cuisine}</span>
        )}
        <h3 className="font-display text-xl font-extrabold tracking-tight leading-snug mt-1">{recipe.title}</h3>
        {recipe.description && <p className="text-sm text-nerd-light/60 mt-1.5 leading-relaxed">{recipe.description}</p>}

        <div className="flex flex-wrap gap-2 mt-4">
          {stats.map((s, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-black/5 text-xs font-medium text-nerd-light/80">
              <s.icon className="w-3.5 h-3.5" /> {s.val}
            </span>
          ))}
        </div>

        {(recipe.tags || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {recipe.tags.slice(0, 4).map((t, i) => (
              <span key={i} className="text-[11px] px-2 py-0.5 rounded-full border border-black/10 text-nerd-light/60">{t}</span>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-nerd-light/40 mb-2">Ingredients</p>
            <ul className="space-y-1.5">
              {(recipe.ingredients || []).map((ing, i) => (
                <li key={i} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="text-nerd-light/85">{ing.name}</span>
                  <span className="font-mono text-xs text-nerd-light/55 shrink-0">{ing.quantity} {ing.unit}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-nerd-light/40 mb-2">Method</p>
            <ol className="space-y-2">
              {(recipe.steps || []).map((s, i) => (
                <li key={i} className="flex gap-2.5 text-sm">
                  <span className="shrink-0 w-5 h-5 rounded-md bg-black/5 flex items-center justify-center text-[11px] font-mono font-semibold text-nerd-light/70">{i + 1}</span>
                  <span className="text-nerd-light/80 leading-relaxed">{s.instruction}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {recipe.tip && (
          <p className="mt-4 text-xs text-nerd-light/55 bg-black/[0.03] rounded-lg px-3 py-2.5 leading-relaxed">
            <span className="font-semibold text-nerd-light/70">Chef tip · </span>{recipe.tip}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 px-5 py-3 border-t border-black/8 bg-black/[0.02]">
        <button data-testid="chat-save-recipe-btn" onClick={save} disabled={saving}
          className="inline-flex items-center gap-1.5 text-sm font-semibold bg-nerd-light text-white px-4 py-2 rounded-full hover:opacity-90 transition-opacity disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : savedId ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          {savedId ? "Saved" : saving ? "Saving…" : "Save recipe"}
        </button>
        {savedId && (
          <button data-testid="chat-cook-btn" onClick={() => navigate(`/recipe/${savedId}`)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold border border-black/12 px-4 py-2 rounded-full hover:bg-black/5 transition-colors">
            <ChefHat className="w-4 h-4" /> View recipe
          </button>
        )}
        {user?.is_admin && (
          <button data-testid="chat-publish-recipe-btn" onClick={publish} disabled={publishing || !!publishedId}
            className="inline-flex items-center gap-1.5 text-sm font-semibold border border-black/12 px-4 py-2 rounded-full hover:bg-black/5 transition-colors disabled:opacity-60">
            {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : publishedId ? <Check className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
            {publishedId ? "Published to Home" : publishing ? "Publishing…" : "Publish to Home"}
          </button>
        )}
        {publishedId && (
          <button data-testid="chat-view-published-btn" onClick={() => navigate(`/recipe/${publishedId}`)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold border border-black/12 px-4 py-2 rounded-full hover:bg-black/5 transition-colors">
            <ChefHat className="w-4 h-4" /> View on Home
          </button>
        )}
      </div>
    </div>
  );
}
